/**
 * A section's header: its label as a drag handle with a right-click menu, or —
 * while renaming — a text input. Enter or blur saves; Escape cancels.
 */
import React, { useEffect, useRef, useState } from 'react';

import {
  ContextMenuItemButtons,
  ContextMenuShell,
  type ContextMenuItem,
} from '@features/workspace';
import { SECTION_LABEL_MAX } from '@shared/navigation/railLayout';

import type { useSortable } from '@dnd-kit/sortable';

interface SectionHeaderRowProps {
  label: string;
  editing: boolean;
  dragListeners: ReturnType<typeof useSortable>['listeners'];
  menuItems: ContextMenuItem[];
  onStartRename: () => void;
  onRename: (label: string) => void;
  onCancelRename: () => void;
}

/** The rename input: focused and selected when it appears. */
const SectionLabelInput: React.FC<{
  label: string;
  onCommit: (label: string) => void;
  onCancel: () => void;
}> = ({ label, onCommit, onCancel }) => {
  const ref = useRef<HTMLInputElement>(null);
  const cancelled = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return (
    <input
      ref={ref}
      aria-label="Section name"
      defaultValue={label}
      maxLength={SECTION_LABEL_MAX}
      onKeyDown={e => {
        if (e.key === 'Escape') cancelled.current = true;
        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
      }}
      onBlur={e => (cancelled.current ? onCancel() : onCommit(e.currentTarget.value))}
      className="border-primary bg-card text-foreground h-6 w-full rounded border px-1.5 text-xs focus:outline-none"
    />
  );
};

/** A section header, or its rename input while renaming. */
export const SectionHeaderRow: React.FC<SectionHeaderRowProps> = ({
  label,
  editing,
  dragListeners,
  menuItems,
  onStartRename,
  onRename,
  onCancelRename,
}) => {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  if (editing) {
    return <SectionLabelInput label={label} onCommit={onRename} onCancel={onCancelRename} />;
  }
  return (
    <>
      <button
        type="button"
        onDoubleClick={onStartRename}
        onContextMenu={e => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        className="text-sidebar-foreground/60 hover:bg-sidebar-accent/60 flex h-6 w-full cursor-grab items-center truncate rounded px-2 text-left text-xs tracking-wide uppercase select-none active:cursor-grabbing"
        {...dragListeners}
      >
        {label}
      </button>
      {menu && (
        <ContextMenuShell x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <ContextMenuItemButtons items={menuItems} onClose={() => setMenu(null)} />
        </ContextMenuShell>
      )}
    </>
  );
};
